import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { RecipeCard } from '@/components/develop/RecipeCard';
import { RecipeEditor } from '@/components/develop/RecipeEditor';
import { DevelopmentTimer } from '@/components/develop/DevelopmentTimer';
import { DevelopmentHistory } from '@/components/develop/DevelopmentHistory';
import { useDevelopRecipes } from '@/hooks/useDevelopRecipes';
import { DevelopmentRecipe, DevelopmentSession } from '@/types/develop';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Plus, Moon, Search, X } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/hooks/useTranslation';
import { Helmet } from 'react-helmet';
import { useDarkroomMode } from '@/hooks/useDarkroomMode';
import { useLocation } from 'react-router-dom';
import { MobileRecipeCard } from '@/components/develop/MobileRecipeCard';

const Develop: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { darkroomMode, toggleDarkroomMode } = useDarkroomMode();
  const location = useLocation();
  
  // Check if we're on the develop page
  useEffect(() => {
    // No action needed - the useDarkroomMode hook will handle restrictions
  }, [location.pathname]);
  
  const { 
    recipes, 
    sessions, 
    loading, 
    createRecipe, 
    updateRecipe, 
    deleteRecipe,
    addSession,
    deleteSession
  } = useDevelopRecipes();
  
  // UI state
  const [activeTab, setActiveTab] = useState('recipes');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Editing state
  const [isCreating, setIsCreating] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState<DevelopmentRecipe | null>(null);
  const [activeRecipe, setActiveRecipe] = useState<DevelopmentRecipe | null>(null);
  
  // Filter and sort recipes
  const filteredRecipes = recipes.filter(recipe => 
    recipe.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (recipe.filmStock && recipe.filmStock.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (recipe.developer && recipe.developer.toLowerCase().includes(searchQuery.toLowerCase()))
  );
  
  const sortedRecipes = [...filteredRecipes].sort((a, b) => {
    // First sort by favorite status
    if (a.isFavorite && !b.isFavorite) return -1;
    if (!a.isFavorite && b.isFavorite) return 1;
    
    // Then sort by most recently updated
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
  
  // Handle recipe actions
  const handleStartRecipe = (recipe: DevelopmentRecipe) => {
    setActiveRecipe(recipe);
    
    // Update recipe's updatedAt timestamp
    updateRecipe(recipe.id, {
      // We need to omit updatedAt from the updates since it's handled internally by updateRecipe
      name: recipe.name,
    });
  };
  
  const handleEditRecipe = (recipe: DevelopmentRecipe) => {
    setEditingRecipe(recipe);
    setIsCreating(false);
  };
  
  const handleDeleteRecipe = (id: string) => {
    if (window.confirm('Are you sure you want to delete this recipe? This action cannot be undone.')) {
      deleteRecipe(id);
    }
  };
  
  const handleToggleFavorite = (recipe: DevelopmentRecipe) => {
    updateRecipe(recipe.id, {
      isFavorite: !recipe.isFavorite
    });
  };
  
  const handleSaveRecipe = (recipe: Omit<DevelopmentRecipe, 'id' | 'createdAt' | 'updatedAt' | 'isUserCreated'>) => {
    if (editingRecipe) {
      // When updating, preserve the isUserCreated flag from the original recipe
      updateRecipe(editingRecipe.id, {
        ...recipe,
        isUserCreated: editingRecipe.isUserCreated
      });
      setEditingRecipe(null);
    } else {
      createRecipe(recipe);
      setIsCreating(false);
    }
    
    // Switch back to recipes tab
    setActiveTab('recipes');
  };
  
  const handleCompleteSession = (sessionData: Omit<DevelopmentSession, 'id'>) => {
    addSession(sessionData);
    
    // Switch to history tab after completing a session
    setActiveTab('history');
  };
  
  const handleDeleteSession = (id: string) => {
    if (window.confirm('Are you sure you want to delete this development session? This action cannot be undone.')) {
      deleteSession(id);
    }
  };
  
  // If a recipe is active, show the timer
  if (activeRecipe) {
    return (
      <div className={cn(
        'container mx-auto px-0 md:px-4 py-4 min-h-screen',
        darkroomMode && 'bg-black'
      )}>
        <Helmet>
          <title>Film Development Timer - Safelight</title>
          <meta name="description" content="Track your film development process with Safelight's development timer." />
        </Helmet>
        
        <DevelopmentTimer 
          recipe={activeRecipe}
          onComplete={handleCompleteSession}
          onBack={() => setActiveRecipe(null)}
          onEditRecipe={() => {
            setEditingRecipe(activeRecipe);
            setActiveRecipe(null);
          }}
        />
      </div>
    );
  }
  
  // If creating or editing a recipe, show the editor
  if (isCreating || editingRecipe) {
    return (
      <div className={cn(
        'container mx-auto px-0 md:px-4 py-4 min-h-screen',
        darkroomMode && 'bg-black'
      )}>
        <Helmet>
          <title>{editingRecipe ? 'Edit Recipe' : 'Create Recipe'} - Safelight</title>
          <meta name="description" content="Create and customize film development recipes with Safelight." />
        </Helmet>
        
        <RecipeEditor 
          recipe={editingRecipe || undefined}
          onSave={handleSaveRecipe}
          onCancel={() => {
            setEditingRecipe(null);
            setIsCreating(false);
          }}
          darkMode={darkroomMode}
        />
      </div>
    );
  }
  
  // Main development page with tabs
  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className={cn(
        'container mx-auto px-2 md:px-4 py-4 min-h-screen',
        darkroomMode && 'bg-black'
      )}
    >
      <Helmet>
        <title>Film Development - Safelight</title>
        <meta name="description" content="Manage film development recipes and track your development history with Safelight." />
      </Helmet>
      
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 space-y-2 md:space-y-0">
        <h1 className={cn(
          'text-2xl font-bold',
          darkroomMode && 'text-red-400'
        )}>
          Film Development
        </h1>
        
        <div className="flex gap-2 flex-wrap w-full md:w-auto justify-end">
          <Button 
            variant={darkroomMode ? "destructive" : "outline"} 
            size="sm" 
            onClick={toggleDarkroomMode}
            className={cn(
              darkroomMode ? 'bg-red-900 hover:bg-red-800 text-red-100 border-red-900' : '',
              'flex-1 md:flex-none'
            )}
          >
            <Moon className="h-4 w-4 mr-1" />
            {isMobile ? 'Darkroom' : 'Darkroom Mode'}
          </Button>
          
          <Button 
            onClick={() => {
              setIsCreating(true);
              setEditingRecipe(null);
            }}
            size="sm"
            className={cn(
              darkroomMode ? 'bg-red-900 hover:bg-red-800 text-red-100' : '',
              'flex-1 md:flex-none'
            )}
          >
            <Plus className="h-4 w-4 mr-1" />
            {isMobile ? 'New' : 'New Recipe'}
          </Button>
        </div>
      </div>
      
      <Tabs 
        defaultValue="recipes" 
        value={activeTab} 
        onValueChange={setActiveTab}
        className="mb-4"
      >
        <TabsList className={cn(
          'w-full',
          darkroomMode && 'bg-red-950'
        )}>
          <TabsTrigger 
            value="recipes"
            className={cn(
              'flex-1',
              darkroomMode && 'data-[state=active]:bg-black data-[state=active]:text-red-400'
            )}
          >
            Recipes
          </TabsTrigger>
          <TabsTrigger 
            value="history"
            className={cn(
              'flex-1',
              darkroomMode && 'data-[state=active]:bg-black data-[state=active]:text-red-400'
            )}
          >
            Development History
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="recipes" className="space-y-4">
          <div className="relative">
            <Search className={cn(
              'absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground',
              darkroomMode && 'text-red-700'
            )} />
            
            <Input 
              placeholder={isMobile ? "Search recipes..." : "Search recipes by name, film, or developer..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={cn(
                'pl-9 pr-9',
                darkroomMode && 'bg-black border-red-900 text-red-500 placeholder:text-red-800'
              )}
            />
            
            {searchQuery && (
              <Button 
                variant="ghost" 
                size="icon" 
                className={cn(
                  'absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6',
                  darkroomMode && 'text-red-700 hover:text-red-400 hover:bg-red-950'
                )}
                onClick={() => setSearchQuery('')}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          
          {loading ? (
            <div className={cn(
              'text-center py-8',
              darkroomMode && 'text-red-500'
            )}>
              Loading recipes...
            </div>
          ) : sortedRecipes.length === 0 ? (
            <div className={cn(
              'text-center py-8 space-y-4',
              darkroomMode && 'text-red-500'
            )}>
              {searchQuery ? (
                <>
                  <p>No recipes match your search query.</p>
                  <Button 
                    variant="outline" 
                    onClick={() => setSearchQuery('')}
                    className={darkroomMode ? 'border-red-900 text-red-500 hover:bg-red-950' : ''}
                  >
                    Clear Search
                  </Button>
                </>
              ) : (
                <>
                  <p>No recipes found. Create your first development recipe to get started.</p>
                  <Button 
                    onClick={() => {
                      setIsCreating(true);
                      setEditingRecipe(null);
                    }}
                    className={darkroomMode ? 'bg-red-900 hover:bg-red-800 text-red-100' : ''}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Create Recipe
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {sortedRecipes.map(recipe => (
                isMobile ? (
                  <MobileRecipeCard
                    key={recipe.id}
                    recipe={recipe}
                    onStart={() => handleStartRecipe(recipe)}
                    onEdit={() => handleEditRecipe(recipe)}
                    onDelete={() => handleDeleteRecipe(recipe.id)}
                    onToggleFavorite={() => handleToggleFavorite(recipe)}
                    darkMode={darkroomMode}
                  />
                ) : (
                  <RecipeCard 
                    key={recipe.id}
                    recipe={recipe}
                    onStart={() => handleStartRecipe(recipe)}
                    onEdit={() => handleEditRecipe(recipe)}
                    onDelete={() => handleDeleteRecipe(recipe.id)}
                    onToggleFavorite={() => handleToggleFavorite(recipe)}
                    darkMode={darkroomMode}
                  />
                )
              ))}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="history">
          <DevelopmentHistory 
            sessions={sessions}
            recipes={recipes}
            onDelete={handleDeleteSession}
            darkMode={darkroomMode}
          />
        </TabsContent>
      </Tabs>
    </motion.div>
  );
};

export default Develop;
